import { useMemo } from 'react'
import type { ScheduleItem } from './useTodayBoardData'
import type { HourGroup } from './types'

/**
 * Groups ScheduleItem[] by hour of `sortTime`.
 * Returns HourGroup[] sorted chronologically.
 * Items without sortTime go into an "Unscheduled" group at the end.
 */
export function useHourGroups(items: ScheduleItem[]): HourGroup<ScheduleItem>[] {
  return useMemo(() => {
    const map = new Map<number, ScheduleItem[]>()
    const unscheduled: ScheduleItem[] = []

    for (const item of items) {
      if (!item.sortTime) {
        unscheduled.push(item)
        continue
      }
      const hour = new Date(item.sortTime).getHours()
      const group = map.get(hour)
      if (group) {
        group.push(item)
      } else {
        map.set(hour, [item])
      }
    }

    const groups: HourGroup<ScheduleItem>[] = []

    const sortedHours = [...map.keys()].sort((a, b) => a - b)
    for (const hour of sortedHours) {
      groups.push({
        hour,
        label: `${String(hour).padStart(2, '0')}:00`,
        items: map.get(hour)!,
      })
    }

    if (unscheduled.length > 0) {
      groups.push({
        hour: -1,
        label: 'Unscheduled',
        items: unscheduled,
      })
    }

    return groups
  }, [items])
}
